import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * Parses a cart item count from API / DOM values.
 * @param {unknown} value
 * @returns {number | null}
 */
export function parseCartItemCount(value) {
  const count = Number(value);
  return Number.isFinite(count) ? count : null;
}

/**
 * Updates every cart bubble on the page immediately.
 * @param {number} totalCount
 * @param {boolean} [animate=true]
 */
export async function syncCartBubbleCount(totalCount, animate = true) {
  document.querySelectorAll('.cart-bubble').forEach((bubble) => {
    bubble.classList.toggle('visually-hidden', totalCount === 0);
  });

  document.querySelectorAll('.cart-bubble__text-count').forEach((countEl) => {
    countEl.classList.toggle('hidden', totalCount === 0);
    countEl.textContent = totalCount > 0 && totalCount < 100 ? String(totalCount) : '';
  });

  document.querySelectorAll('cart-icon').forEach((icon) => {
    icon.classList.toggle('header-actions__cart-icon--has-cart', totalCount > 0);
    /** @type {CartIcon} */ (icon).currentCartCount = totalCount;
  });

  sessionStorage.setItem(
    'cart-count',
    JSON.stringify({
      value: String(totalCount),
      timestamp: Date.now(),
    })
  );

  if (!animate || totalCount === 0) return;

  const bubbleTexts = document.querySelectorAll('.cart-bubble__text');
  if (bubbleTexts.length === 0) return;

  await new Promise((resolve) => requestAnimationFrame(resolve));

  document.querySelectorAll('.cart-bubble').forEach((bubble) => {
    bubble.classList.add('cart-bubble--animating');
  });

  await Promise.all(
    Array.from(bubbleTexts).map((text) => onAnimationEnd(/** @type {HTMLElement} */ (text)))
  );

  document.querySelectorAll('.cart-bubble').forEach((bubble) => {
    bubble.classList.remove('cart-bubble--animating');
  });
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  #currentCount = 0;

  /** @type {number} */
  get currentCartCount() {
    return this.#currentCount;
  }

  set currentCartCount(value) {
    this.#currentCount = value;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.#currentCount = parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10) || 0;
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Resolves the authoritative cart count from a cart update event.
   * @param {CartAddEvent | CartUpdateEvent} event
   * @returns {number}
   */
  #resolveCartCount(event) {
    const resource = /** @type {{ item_count?: unknown } | undefined} */ (event.detail.resource);
    const fromResource = parseCartItemCount(resource?.item_count);
    if (fromResource !== null) return fromResource;

    if (event.detail.data?.source === 'cart-items-component') {
      const fromComponent = parseCartItemCount(event.detail.data?.itemCount);
      if (fromComponent !== null) return fromComponent;
    }

    const fromEvent = parseCartItemCount(event.detail.data?.itemCount);
    if (fromEvent !== null && event.detail.data?.source !== 'product-form-component') {
      return fromEvent;
    }

    if (event.detail.data?.source === 'product-form-component') {
      const added = parseCartItemCount(event.detail.data?.itemCount) ?? 0;
      return this.#currentCount + added;
    }

    return this.#currentCount;
  }

  /**
   * Handles the cart update event.
   * @param {CartAddEvent | CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    if (!(event instanceof CartAddEvent || event instanceof CartUpdateEvent)) return;
    if (event.detail?.data?.didError) return;

    const totalCount = this.#resolveCartCount(event);
    this.renderCartBubble(totalCount, true);
  };

  /**
   * Updates every cart bubble on the page (header icon + mobile bottom nav).
   * @param {number} totalCount - The total number of items in the cart.
   * @param {boolean} [animate=true] - Whether to play the bubble animation.
   */
  renderCartBubble = async (totalCount, animate = true) => {
    this.#currentCount = totalCount;
    await syncCartBubbleCount(totalCount, animate);
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    const firstCountEl = document.querySelector('.cart-bubble__text-count');
    const visibleCount = firstCountEl?.textContent ?? '';

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (value === visibleCount) return;

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
